import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import {
  ShopOrderCsMemo,
  ShopOrderCsMemoCreateDto,
  ShopOrderCsMemoLogItem,
  ShopOrderCsMemoLogParams,
  ShopOrderCsMemoLogResponse,
  ShopOrderCsMemoLogSummary,
} from './shopOrderCsMemo.interface';

// 화환 주문 CS 응대 메모 (Admin + 셀러 3롤 공용, 셀러는 자기 매장 주문만)
// 구서버엔 없는 경로 — 404 가능(호출부 가드). 스펙: docs/shop-order-cs-memo-spec.md
const BASE = `/${LowerRole.Admin}/shop-order-cs-memo`;

export const isShopCsMemoNotFound = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 404;

/** 삭제 엔드포인트 미배포 판별 (404 = 라우트 없음, 405 = 메서드 미허용) */
export const isShopCsMemoDeleteUnsupported = (e: unknown): boolean => {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 404 || status === 405;
};

/** 주문 단위 타임라인 (createdAt 오름차순) */
export const getShopOrderCsMemos = async (
  shopOrder: string,
): Promise<ShopOrderCsMemo[]> => {
  const params = new URLSearchParams({ shopOrder });
  const { data } = await api_.get<ShopOrderCsMemo[]>(`${BASE}?${params}`);
  return data;
};

/**
 * 주문 다건 배치 조회 (목록·카드 화면용, 100개 상한).
 * 메모 없는 주문도 빈 배열로 키가 온다. 남의 매장 주문은 403이 아니라 빈 배열.
 */
export const getShopOrderCsMemosBatch = async (
  shopOrderIds: string[],
): Promise<Record<string, ShopOrderCsMemo[]>> => {
  const params = new URLSearchParams({ shopOrders: shopOrderIds.join(',') });
  const { data } = await api_.get<Record<string, ShopOrderCsMemo[]>>(
    `${BASE}/batch?${params}`,
  );
  return data;
};

/** CS 응대 로그 — 기간·검색으로 훑는 목록 (createdAt 내림차순, 셀러는 자기 매장만) */
/** 목록·요약이 공유하는 조회 조건 (페이징만 목록 쪽에서 덧붙인다) */
const logQueryString = (params: ShopOrderCsMemoLogParams): URLSearchParams => {
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.q) query.set('q', params.q);
  //* 지정 시 서버가 기간 필터를 무시하고 그 주문의 전체 이력을 준다
  if (params.shopOrder) query.set('shopOrder', params.shopOrder);
  if (params.adminOnly) query.set('adminOnly', '1');
  return query;
};

export const getShopOrderCsMemoLog = async (
  params: ShopOrderCsMemoLogParams,
): Promise<ShopOrderCsMemoLogResponse> => {
  const query = logQueryString(params);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));

  const { data } = await api_.get<ShopOrderCsMemoLogResponse>(`${BASE}/log?${query}`);
  return data;
};

/**
 * 요약 집계 — 분포 칩을 **서버 집계 1요청**으로 받는다 (답례품 로그와 같은 계약).
 * 미배포 서버는 404 → `null`, 한 번 404면 세션 동안 다시 두드리지 않는다.
 */
let summarySupported: boolean | null = null;

export const getShopOrderCsMemoLogSummary = async (
  params: ShopOrderCsMemoLogParams,
): Promise<ShopOrderCsMemoLogSummary | null> => {
  if (summarySupported === false) return null;
  try {
    const { data } = await api_.get<ShopOrderCsMemoLogSummary>(
      `${BASE}/log/summary?${logQueryString(params)}`,
    );
    //* 라우트는 있는데 계약이 다른 서버면 칩이 조용히 비는 대신 페이지 기준으로 넘긴다
    if (!data?.counts) {
      summarySupported = false;
      return null;
    }
    summarySupported = true;
    return data;
  } catch (e) {
    if (!isShopCsMemoNotFound(e)) throw e;
    summarySupported = false;
    return null;
  }
};

/** 한 번에 받아오는 페이지 크기 / 최대 페이지 수 (= 최대 2000건) */
export const SHOP_CS_MEMO_LOG_ALL_PAGE_SIZE = 200;
export const SHOP_CS_MEMO_LOG_ALL_MAX_PAGES = 10;

/**
 * 조회 조건 전체를 페이지 넘겨가며 모아 온다 (통계 패널·엑셀 공용).
 * 상한에 걸리면 잘린 채로 돌려주고 `capped`로 알린다 — 화면이 "전체 집계"라고 말하지 않게.
 */
export const getShopOrderCsMemoLogAll = async (
  params: ShopOrderCsMemoLogParams,
): Promise<{ items: ShopOrderCsMemoLogItem[]; total: number; capped: boolean }> => {
  const items: ShopOrderCsMemoLogItem[] = [];
  let total = 0;

  for (let page = 1; page <= SHOP_CS_MEMO_LOG_ALL_MAX_PAGES; page += 1) {
    const res = await getShopOrderCsMemoLog({
      ...params,
      page,
      limit: SHOP_CS_MEMO_LOG_ALL_PAGE_SIZE,
    });
    total = res.total;
    items.push(...res.items);
    if (items.length >= res.total || res.items.length < SHOP_CS_MEMO_LOG_ALL_PAGE_SIZE) {
      break;
    }
  }

  return { items, total, capped: items.length < total };
};

export const createShopOrderCsMemo = async (
  dto: ShopOrderCsMemoCreateDto,
): Promise<ShopOrderCsMemo> => {
  const { data } = await api_.post<ShopOrderCsMemo>(BASE, dto);
  return data;
};

/** 소프트 삭제 (오기입 취소) — 중복 호출 멱등 */
export const deleteShopOrderCsMemo = async (id: string): Promise<void> => {
  await api_.delete(`${BASE}/${id}`);
};
